import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";

const STATUS_CLS = {
  dirty: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  in_progress: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  clean: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  inspecting: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
};

const PRIORITY_CLS = {
  high: "text-orange-300",
  med: "text-[#D4AF37]",
  low: "text-white/50",
};

const STAFF = ["Lakshmi M.", "Ravi P.", "Sunita K.", "Devi R.", "Arjun S."];

export default function Housekeeping() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/housekeeping"); setData(data); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function setStatus(id, status) {
    try { await api.patch(`/housekeeping/${id}`, { status }); toast.success(`→ ${status}`); load(); }
    catch { toast.error("Failed"); }
  }
  async function assign(id, person) {
    try { await api.patch(`/housekeeping/${id}`, { assigned_to: person }); toast.success("Assigned"); load(); }
    catch { toast.error("Failed"); }
  }

  if (loading || !data) return <div className="text-white/50">Loading…</div>;
  const s = data.summary;

  return (
    <div className="space-y-8" data-testid="housekeeping-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Housekeeping</div>
        <h1 className="font-display text-3xl md:text-4xl">Room status &amp; tasks</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {[
          { k: "dirty", label: "Dirty rooms", n: s.dirty || 0 },
          { k: "in_progress", label: "In progress", n: s.in_progress || 0 },
          { k: "clean", label: "Clean & ready", n: s.clean || 0 },
          { k: "inspecting", label: "Inspecting", n: s.inspecting || 0 },
        ].map((x) => (
          <div key={x.k} className="bg-[#12182B] border border-white/5 rounded-xl p-5" data-testid={`hk-sum-${x.k}`}>
            <div className="text-xs uppercase tracking-widest text-white/50">{x.label}</div>
            <div className="font-display text-3xl mt-3">{x.n}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="hk-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                <th className="px-5 py-4">Room</th>
                <th className="px-5 py-4">Type</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Priority</th>
                <th className="px-5 py-4">Assigned to</th>
                <th className="px-5 py-4">Task</th>
                <th className="px-5 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((t) => (
                <tr key={t.id} className="border-b border-white/5 last:border-none hover:bg-[#1A2235]" data-testid={`hk-row-${t.room_number}`}>
                  <td className="px-5 py-4 font-medium">{t.room_number}</td>
                  <td className="px-5 py-4 text-white/70">{t.room_type}</td>
                  <td className="px-5 py-4">
                    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${STATUS_CLS[t.status] || STATUS_CLS.dirty}`}>{t.status.replace("_", " ")}</span>
                  </td>
                  <td className="px-5 py-4"><span className={`text-xs uppercase ${PRIORITY_CLS[t.priority] || "text-white/60"}`}>{t.priority}</span></td>
                  <td className="px-5 py-4 text-white/70">
                    {t.assigned_to || (
                      <select onChange={(e) => assign(t.id, e.target.value)} defaultValue="" className="bg-[#0a0f1c] border border-white/10 rounded px-2 py-1 text-xs">
                        <option value="">Assign…</option>
                        {STAFF.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-5 py-4 text-white/70">{t.task_type}</td>
                  <td className="px-5 py-4 text-right">
                    {t.status === "dirty" && <button onClick={() => setStatus(t.id, "in_progress")} className="text-xs text-[#D4AF37] hover:text-[#F2D06B]" data-testid={`hk-start-${t.room_number}`}>Start</button>}
                    {t.status === "in_progress" && <button onClick={() => setStatus(t.id, "clean")} className="text-xs text-emerald-300 hover:text-emerald-200" data-testid={`hk-done-${t.room_number}`}>Mark done</button>}
                    {t.status === "clean" && <button onClick={() => setStatus(t.id, "inspecting")} className="text-xs text-[#D4AF37] hover:text-[#F2D06B]">Inspect</button>}
                    {t.status === "inspecting" && <button onClick={() => setStatus(t.id, "dirty")} className="text-xs text-white/50 hover:text-white">Reset</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
